import React, { useEffect } from 'react';
import styled from 'styled-components';
import VariableRequest from '@requests/global_variables';

import { LeftNavbar, TopNavbar } from '../../components/Navbars';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { listWorkspaceUsers } from '@requests/roles';
import { getBuckets } from '@requests/buckets/buckets.requests';
import { GetBucketsParams } from '@requests/buckets';

const Container = styled.div`
  min-height: 100vh;
  max-height: 100vh;

  align-items: stretch;
  display: flex;
  flex-direction: row;
  height: 100%;
  padding: 0;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;

  flex: 1 1 auto;
  min-width: 0;
  padding-left: 60px;
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  min-height: 0;
  align-items: stretch;
`;

interface Props {
  header: string;
  actions?: React.ReactNode;
  body: React.ReactNode;
}

export const prefetchWorkspaceQueries = (service_id: string | undefined, queryClient: QueryClient) => {
  const userRolesQK = ['roles', { serviceId: service_id }];
  const bucketsQK: [string, string, GetBucketsParams] = ['globalFiles', 'buckets', { service_id: service_id || '' }];
  const globalVariablesQK = ['globalVariables', service_id] as [string, string | undefined];

  queryClient.prefetchQuery({ queryKey: globalVariablesQK, queryFn: VariableRequest.listVariables });
  queryClient.prefetchQuery({ queryKey: userRolesQK, queryFn: listWorkspaceUsers });
  queryClient.prefetchQuery({ queryKey: bucketsQK, queryFn: getBuckets });
};

export const LayoutWorkspace: React.FC<Props> = ({ header, actions, body }) => {
  const { service_id } = useParams();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaces = useSelector((state: State) => state.flow.services);
  const flowLoading = useSelector((state: State) => state.flow.flowLoading);

  /**
   * This validates that the workspace identifier found in the URL path is valid
   * Redirects back to home if invalid workspace is found
   */

  useEffect(() => {
    if (!service_id || flowLoading) return;

    if (!workspaces.find((workspace) => workspace.service_id === service_id)) {
      navigate('/');
    }
  }, [service_id, workspaces, flowLoading]);

  /**
   * Use this useEffect to prefetch queries before the component is loaded
   * Permissions and buckets are currently loaded immediately
   */

  useEffect(() => {
    prefetchWorkspaceQueries(service_id, queryClient);
  }, [service_id, queryClient]);

  return (
    <Container>
      <LeftNavbar />
      <Wrapper>
        <TopNavbar header={header} actions={actions} />
        <Body>{body}</Body>
      </Wrapper>
    </Container>
  );
};
